import {
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon, XIcon } from 'lucide-react';
import { SortIcon } from '../SortIcon';

export interface SortChipOption {
  /** Stable identity used for `value` comparison. Maps to a sortBy + direction tuple at the call site. */
  id: string;
  label: string;
  /** Direction the option sorts in. Drives the chip's trigger icon (chevron up/down) when this option is active. */
  direction: 'asc' | 'desc';
  icon?: ReactNode;
}

export interface SortChipPopoverProps {
  /** Used for aria fallback + the trigger tooltip prefix (e.g. "Sort"). */
  label?: string;
  options: ReadonlyArray<SortChipOption>;
  value: string | null;
  onChange: (next: string | null) => void;
  /**
   * Spell the state out on the trigger — `Sort` while nothing is picked,
   * `Sort by: {option}` once something is, the same shape `FilterChipPopover`
   * uses for Group by. Off by default: the icon-only chip is what most
   * toolbars already render, and widening it would move every one of them.
   */
  showTriggerLabel?: boolean;
  /**
   * What the trigger reads while nothing is picked, matching the sibling prop
   * on `FilterChipPopover`:
   *
   * - `label` (default) — the chip's own name, `Sort`.
   * - `value` — the order the list is actually in, which is `resetOptionLabel`
   *   until something is picked. Use it where the toolbar is expected to state
   *   the current order rather than name the control.
   *
   * Only read when `showTriggerLabel` is set.
   */
  triggerLabelStyle?: 'label' | 'value';
  /** Label for the reset row. Defaults to "Default" (server-side default order). */
  resetOptionLabel?: string;
  /** Tooltip text on the trigger button. Defaults to "Sort by". */
  triggerTooltip?: string;
  triggerAriaLabel?: string;
  /** Frame height. Defaults to `md`; use `lg` beside a `size="lg"` search input. */
  size?: ChipSize;
}

/**
 * The chip frame's height. `md` is the toolbar default; `lg` matches the 40px
 * `SearchInput size="lg"`, so a chip beside a search field lines up with it
 * instead of floating 2px inside its box.
 */
type ChipSize = 'md' | 'lg';

const CHIP_FRAME_HEIGHT_CLASS: Record<ChipSize, string> = {
  md: 'h-9',
  lg: 'h-10',
};

const CHIP_MAX_WIDTH_CLASS = 'max-w-65';
const FRAME_FOCUS_RING = 'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring';
const INNER_BUTTON_FOCUS_RESET = 'outline-none focus-visible:outline-none';

interface SortChipTriggerButtonProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
  /** React 19 ref-as-prop: forwarded to the underlying `<button>`. */
  ref?: Ref<HTMLButtonElement>;
}

function SortChipTriggerButton({ children, className, ref, ...props }: SortChipTriggerButtonProps) {
  return (
    <button
      ref={ref}
      type="button"
      className={[
        // `h-full` rather than a fixed height: the frame owns the size rung and
        // has a border, so a child pinned to the frame's own height overflows its
        // content box and gets clipped by `overflow-hidden` -- which reads as a
        // label sitting a pixel low with its descenders shaved off.
        'flex h-full min-w-0 flex-1 cursor-pointer items-center gap-1.5 border-0 bg-transparent text-sm text-foreground transition-colors hover:bg-accent',
        INNER_BUTTON_FOCUS_RESET,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}

SortChipTriggerButton.displayName = 'SortChipTriggerButton';

/**
 * Sort sibling of `FilterChipPopover`. Shares the chip frame, active-state
 * styling, and option-row visuals so a `Sort` chip and a `Filter` chip can sit
 * next to each other in a toolbar without visual drift. Differences:
 *
 *   - Single-select only (one sort key at a time).
 *   - No search input — sort menus are short and known.
 *   - Reset row label defaults to "Default" instead of "All", since clearing
 *     a sort means "fall back to the server default order".
 *
 * Caller owns the mapping between `option.id` and an actual sortBy/direction
 * tuple, so this component stays decoupled from any specific sort enum.
 */
export function SortChipPopover(props: SortChipPopoverProps) {
  const { t } = useTranslation('common');

  const [open, setOpen] = useState(false);

  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const triggerLabel = props.label ?? t('ui.sortChipPopover.sort');
  const resetLabel = props.resetOptionLabel ?? t('ui.sortChipPopover.default');
  const triggerLabelStyle = props.triggerLabelStyle ?? 'label';
  const showsValueInTrigger = Boolean(props.showTriggerLabel) && triggerLabelStyle === 'value';
  // Treat the chip as inactive whenever the selected id is missing from the
  // current options list — otherwise removing the active option from the
  // options array leaves a stale primary ring on the chip even though no
  // real sort is applied any more.
  const selectedOption =
    props.value !== null ? (props.options.find((o) => o.id === props.value) ?? null) : null;
  const isActive = selectedOption !== null;

  const closePopover = () => setOpen(false);

  const handleClear = () => {
    props.onChange(null);
    closePopover();
  };

  const selectOption = (id: string) => {
    props.onChange(id);
    closePopover();
  };

  const focusOption = (index: number) => {
    const buttons = optionRefs.current.filter((b): b is HTMLButtonElement => b !== null);
    if (buttons.length === 0) return;
    const clamped = Math.max(0, Math.min(index, buttons.length - 1));
    buttons[clamped]?.focus();
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const buttons = optionRefs.current.filter((b): b is HTMLButtonElement => b !== null);
    if (buttons.length === 0) return;
    const activeIndex = buttons.findIndex((b) => b === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusOption(activeIndex < 0 ? 0 : activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (activeIndex <= 0) {
          focusOption(buttons.length - 1);
        } else {
          focusOption(activeIndex - 1);
        }
        break;
      case 'Home':
        event.preventDefault();
        focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        focusOption(buttons.length - 1);
        break;
    }
  };

  const frameBaseClasses = `inline-flex ${CHIP_FRAME_HEIGHT_CLASS[props.size ?? 'md']} ${CHIP_MAX_WIDTH_CLASS} items-stretch overflow-hidden rounded-md border bg-card transition-[box-shadow,border-color] ${FRAME_FOCUS_RING}`;

  optionRefs.current = [];
  let optionRefIndex = 0;
  const registerOptionRef = (el: HTMLButtonElement | null) => {
    optionRefs.current[optionRefIndex++] = el;
  };

  const tooltipText = props.triggerTooltip ?? t('ui.sortChipPopover.sortBy');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={frameBaseClasses}>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <SortChipTriggerButton
                  aria-label={props.triggerAriaLabel ?? triggerLabel}
                  // Labelled, the chip sits flush-left like the Group by chip;
                  // icon-only, it stays centred in the frame.
                  className={props.showTriggerLabel ? 'pl-3 pr-2' : 'justify-center px-2.5'}
                >
                  {/* The generic indicator, never the active option's own icon:
                      the trigger has to keep reading as the sort control, and
                      the row icons already carry the per-option meaning. */}
                  <SortIcon
                    isActive={selectedOption !== null}
                    direction={selectedOption?.direction ?? 'asc'}
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                  {/* Two weights, one chip: what the control IS carries the
                      label weight, and what it is SET TO carries the value
                      weight. So an unset chip states itself, and a set one
                      lets the value lead with the prefix receding behind it. */}
                  {showsValueInTrigger ? (
                    <span className="min-w-0 truncate font-medium">
                      {selectedOption?.label ?? resetLabel}
                    </span>
                  ) : (
                    <>
                      {props.showTriggerLabel && (
                        <span
                          className={
                            selectedOption
                              ? 'shrink-0 text-muted-foreground'
                              : 'shrink-0 font-medium'
                          }
                        >
                          {selectedOption
                            ? t('ui.sortChipPopover.byPrefix', { label: triggerLabel })
                            : triggerLabel}
                        </span>
                      )}
                      {selectedOption && (
                        <span className="min-w-0 truncate font-medium">{selectedOption.label}</span>
                      )}
                    </>
                  )}
                </SortChipTriggerButton>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* The value-style trigger already reads as the current order, and its
            reset row is how that order is put back — a second control for it
            in the chip frame would say the same thing twice. */}
        {isActive && !showsValueInTrigger && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t('ui.sortChipPopover.clear', { label: triggerLabel.toLowerCase() })}
            className={`flex shrink-0 cursor-pointer items-center justify-center border-0 border-l border-border bg-transparent px-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:text-foreground ${INNER_BUTTON_FOCUS_RESET}`}
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
      <PopoverContent
        side="bottom"
        align="start"
        className="flex w-65 flex-col overflow-hidden p-0"
      >
        <div
          className="min-h-0 max-h-75 flex-1 overflow-y-auto py-1"
          // Same wheel-bypass as FilterChipPopover for portaled popovers
          // rendered inside Radix Dialog, where react-remove-scroll cancels
          // native wheel events outside the dialog tree.
          onWheel={(event) => {
            event.currentTarget.scrollBy({ top: event.deltaY });
          }}
        >
          <button
            ref={registerOptionRef}
            type="button"
            onClick={handleClear}
            onKeyDown={handleListKeyDown}
            className={[
              'flex w-full cursor-pointer items-center gap-2 border-0 px-3 py-1.5 text-left text-sm transition-colors',
              isActive
                ? 'bg-transparent text-foreground hover:bg-accent'
                : 'bg-accent text-primary',
              'focus-visible:bg-accent focus-visible:outline-none',
            ].join(' ')}
          >
            <span className="flex-1 truncate">{resetLabel}</span>
            {!isActive && <CheckIcon className="size-4 shrink-0 text-primary" />}
          </button>

          {props.options.map((option) => {
            const selected = props.value === option.id;
            return (
              <button
                ref={registerOptionRef}
                key={option.id}
                type="button"
                onClick={() => selectOption(option.id)}
                onKeyDown={handleListKeyDown}
                className={[
                  'flex w-full cursor-pointer items-center gap-2 border-0 px-3 py-1.5 text-left text-sm transition-colors',
                  selected
                    ? 'bg-accent text-primary'
                    : 'bg-transparent text-foreground hover:bg-accent',
                  'focus-visible:bg-accent focus-visible:outline-none',
                ].join(' ')}
              >
                {option.icon && <span className="flex shrink-0 items-center">{option.icon}</span>}
                <span className="flex-1 truncate">{option.label}</span>
                {selected && <CheckIcon className="size-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
